import { apiDownload, apiGetOrNull, apiRequest, type IDownload } from '@/domains/api';
import type { IPage } from './oversight-contracts';
import type {
  IAdminSecAdvFirmDetail,
  IAdminSecAdvFirmRow,
  IAdminSecAdvImport,
  IAdminSecAdvStatus,
  ISecAdvCatalogue,
  ISecAdvExportOptions,
  ISecAdvFacets,
  ISecAdvFirmCounts,
  ISecAdvFirmQuery,
  ISecAdvImportQuery,
  SecAdvFacetField
} from './sec-adv-contracts';

/**
 * survey-api's SEC ADV register browse (its SR-214): `/api/v1/admin/sec-adv/*` — the firm list,
 * one firm's card with its prefill preview, the register's state, its import log, and the
 * catalogue that labels a firm's record. The sole reader of that prefix. The one SR-214 member
 * outside it, `secAdvFirms` on each participant document the person hub carries, arrives
 * through `participants-api.ts` with the rest of the hub.
 *
 * **Its own module, for its own policy.** Every route here is `registry.browse` — staff and
 * admin, the same role set as `responses.browse` today, but named apart by survey-api because
 * this reads public reference data rather than survey work, and a later capability grant has to
 * be able to give one without the other. A module per policy is how this section keeps that
 * visible: the day the two sets part, the guard that mirrors this one is the one to move.
 *
 * **Read-only, all of it.** survey-api's surface writes nothing and enqueues nothing — no
 * "import now", no refresh — so neither does anything here.
 *
 * **The catalogue is read once per page load, and its `ETag` is the browser's.** survey-api
 * serves it as one static document with an `ETag` and answers `If-None-Match` with a bodiless
 * 304. This app sends no `If-None-Match` and reads no `ETag`: the browser's HTTP cache stores a
 * response that carries a validator and revalidates it by itself, handing `fetch` the cached
 * body on a 304 — whereas a conditional request made by hand would put that 304 through
 * `client.ts`, which reads every non-2xx as an `ApiError`. Within a page load a module-level
 * promise answers every screen, and is dropped when it fails so the next screen asks again — a
 * success that carries no catalogue counting as a failure.
 * Rejected: keeping it in `localStorage` under its tag — per-origin storage of a deploy-scoped
 * document, buying nothing the HTTP cache does not already give.
 */

const BASE = '/api/v1/admin/sec-adv';

/**
 * Values that could only reach a different route, answered here with the 404's `null`.
 * `encodeURIComponent` keeps anything else inside one segment, but leaves a bare `.` or `..`
 * alone, and the URL parser behind `fetch` removes those — `firms/.` would reach the **list**
 * route and render a page of firms as one (`participants-api.ts` learnt it first).
 */
const NOT_A_SEGMENT = new Set(['', '.', '..']);

/** The catalogue's one read this page load — see the module's comment. */
let catalogueRead: Nullable<Promise<ISecAdvCatalogue>> = null;

/** The catalogue's two lists are there — the least a screen reads it by. The entries inside are
 *  survey-api's, and not judged here. */
function isCatalogue(answer: unknown): answer is ISecAdvCatalogue {
  if (typeof answer !== 'object' || answer === null) return false;
  const { elements, custodians } = answer as Partial<Record<keyof ISecAdvCatalogue, unknown>>;
  return Array.isArray(elements) && Array.isArray(custodians);
}

export const secAdvApi = {
  /** The firm list — its query as `firmParams` spells it. */
  listFirms(
    query: ISecAdvFirmQuery = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminSecAdvFirmRow>> {
    const rendered = firmParams(query).toString();
    return apiRequest<IPage<IAdminSecAdvFirmRow>>(
      `${BASE}/firms${rendered ? `?${rendered}` : ''}`,
      { signal }
    );
  },

  /** One field's values among the firms the query's search and other filters keep, most common
   *  first — a filter chip's options. The search travels as `search`; `q` is `prefix`, which
   *  narrows the values (SR-423). */
  listFacets(
    field: SecAdvFacetField,
    query: ISecAdvFirmQuery,
    prefix: string,
    signal?: AbortSignal
  ): Promise<ISecAdvFacets> {
    const params = firmParams({ ...query, page: undefined, pageSize: undefined, sort: undefined });
    params.delete('columns');
    params.delete(FACET_PARAM[field]);
    params.delete('q');
    if (query.q !== undefined && query.q.trim() !== '') params.set('search', query.q);
    params.set('field', field);
    if (prefix !== '') params.set('q', prefix);
    return apiRequest<ISecAdvFacets>(`${BASE}/firms/facets?${params}`, { signal });
  },

  /** Each card's firms and its signals' under the query's search and filters — the caller leaves
   *  out its card, signals, order, columns and page (`useFirmCounts`) (SR-423). */
  firmCounts(query: ISecAdvFirmQuery, signal?: AbortSignal): Promise<ISecAdvFirmCounts> {
    const rendered = firmParams(query).toString();
    return apiRequest<ISecAdvFirmCounts>(`${BASE}/firms/counts${rendered ? `?${rendered}` : ''}`, {
      signal
    });
  },

  /** The list as a file — CSV or Excel — under the same query (SR-423). */
  exportFirms(query: ISecAdvFirmQuery, options: ISecAdvExportOptions): Promise<IDownload> {
    const params = firmParams(
      options.limit === undefined ? query : { ...query, page: undefined, pageSize: undefined }
    );
    params.set('format', options.format);
    params.set('headers', options.headers);
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    for (const field of options.fields) params.append('fields', field);
    return apiDownload(`${BASE}/firms/export?${params}`);
  },

  /**
   * One firm's card by its CRD, or `null` when no firm in the register holds it — a 404
   * through `apiGetOrNull`, for an unissued CRD and a value that is no CRD at all alike (the
   * route has no 400 arm). Every other status still throws, so a failed read cannot pass for
   * no firm being there.
   *
   * The CRD is sent as **one** encoded path segment, taken as the route handed it over and
   * never decoded again; `''`, `.` and `..` are answered here, since sending them would ask
   * another route.
   */
  getFirm(firmCrd: string, signal?: AbortSignal): Promise<Nullable<IAdminSecAdvFirmDetail>> {
    if (NOT_A_SEGMENT.has(firmCrd)) return Promise.resolve(null);
    return apiGetOrNull<IAdminSecAdvFirmDetail>(
      `${BASE}/firms/${encodeURIComponent(firmCrd)}`,
      { signal }
    );
  },

  /** The register's state as of today (UTC): its daily feed, its filing window, and the
   *  latest jobs of both import types. */
  getStatus(signal?: AbortSignal): Promise<IAdminSecAdvStatus> {
    return apiRequest<IAdminSecAdvStatus>(`${BASE}/status`, { signal });
  },

  /**
   * The import log, latest `generatedOn` first. `kind` is sent whenever the caller names one,
   * as named — a value outside `firm` / `filing` is survey-api's to refuse, with a 400 whose
   * `problems[]` says why.
   */
  listImports(
    query: ISecAdvImportQuery = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminSecAdvImport>> {
    const params = new URLSearchParams();
    if (query.kind !== undefined && query.kind !== '') params.append('kind', query.kind);
    if (query.page !== undefined) params.append('page', String(query.page));
    if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));

    const rendered = params.toString();
    return apiRequest<IPage<IAdminSecAdvImport>>(
      `${BASE}/imports${rendered ? `?${rendered}` : ''}`,
      { signal }
    );
  },

  /**
   * The labels for a firm's record and a custodian's members — one read per page load, shared
   * by every screen that asks, and forgotten when it fails (see the module's comment).
   *
   * It takes no `AbortSignal` on purpose: the read is shared, and the first screen to leave
   * would otherwise cancel it for every other one. A caller that has gone simply ignores the
   * answer.
   */
  getCatalogue(): Promise<ISecAdvCatalogue> {
    if (catalogueRead === null) {
      // Checked inside the shared promise, so a success that carries no catalogue — an empty
      // body, which the client reads as `null`, or a document without its two lists — is a
      // failure like any other: forgotten here, and said by the screen. Kept, a `null` would hold
      // every screen's filing on a skeleton for the page load, and `{}` throw where it is read.
      const read = apiRequest<unknown>(`${BASE}/catalogue`).then((answer) => {
        if (!isCatalogue(answer)) {
          throw new Error('The answer held no catalogue');
        }
        return answer;
      });
      catalogueRead = read;
      read.catch(() => {
        if (catalogueRead === read) catalogueRead = null;
      });
    }
    return catalogueRead;
  }
};

/** The list parameter a facet's field narrows — left out of its own values' read. */
const FACET_PARAM: Record<SecAdvFacetField, string> = {
  city: 'city',
  state: 'state',
  firmType: 'firmType',
  services: 'service',
  surveyStatus: 'surveyStatus'
};

/**
 * The firm list's query as survey-api reads it — `q` only when non-empty, each repeated filter
 * once per value, `hasParticipant` whenever it is named, either way: it has no default, and
 * `false` is a question of its own.
 */
function firmParams(query: ISecAdvFirmQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q !== '') params.append('q', query.q);
  for (const firmType of query.firmType ?? []) params.append('firmType', firmType);
  if (query.hasParticipant !== undefined) {
    params.append('hasParticipant', String(query.hasParticipant));
  }
  if (query.filedSince !== undefined) params.append('filedSince', query.filedSince);
  if (query.aumMin !== undefined) params.append('aumMin', String(query.aumMin));
  if (query.aumMax !== undefined) params.append('aumMax', String(query.aumMax));
  for (const city of query.city ?? []) params.append('city', city);
  for (const state of query.state ?? []) params.append('state', state);
  for (const service of query.service ?? []) params.append('service', service);
  for (const status of query.surveyStatus ?? []) params.append('surveyStatus', status);
  if (query.card !== undefined) params.append('card', query.card);
  for (const signal of query.signal ?? []) params.append('signal', signal);
  if (query.sort !== undefined) params.append('sort', query.sort);
  if (query.sort !== undefined && query.dir !== undefined) params.append('dir', query.dir);
  for (const column of query.columns ?? []) params.append('columns', column);
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));
  return params;
}
